import { useState, useCallback, useRef, useEffect } from 'react';
import { isAxiosError } from 'axios';
import {
	Box,
	Typography,
	Tabs,
	Tab,
	Paper,
	Button,
	LinearProgress,
	Alert,
	AlertTitle,
	Card,
	CardContent,
	Stack,
	IconButton,
	Tooltip,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	TextField,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Switch,
	FormControlLabel
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { downloadImportErrorsCsv } from '../../utils/import-errors-csv';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InfoIcon from '@mui/icons-material/Info';
import { BasePageLayout, TabPanel } from '@/components/common';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useUser } from '@/lib/auth-client';
import {
	importApi,
	dataManagementApi,
	ImportType,
	ImportTypeLabels,
	ValidationResult,
	ImportProgress,
	ImportError,
	TableStats,
	KetQuaPhanLoai
} from '@/services/importApi';
import ImportUpdateConfirmDialog from '@/components/Dialogs/ImportUpdateConfirmDialog';

interface ImportTabState {
	file: File | null;
	validationResult: ValidationResult | null;
	isValidating: boolean;
	isImporting: boolean;
	importProgress: ImportProgress | null;
	importResult: ImportProgress | null;
	/** Chỉ dùng cho "Chuyển đổi mã THPT" — mặc định TẮT, xem dialog xác nhận. */
	chuyenBiDanhCu: boolean;
}

const initialTabState: ImportTabState = {
	file: null,
	validationResult: null,
	isValidating: false,
	isImporting: false,
	importProgress: null,
	importResult: null,
	chuyenBiDanhCu: false
};

const allImportTypes: ImportType[] = [
	'province',
	'highschool',
	'faculty',
	'major',
	'admission-method',
	'sinh-vien',
	'chuyen-doi-thpt',
	'top149'
];

const managerAllowedTypes: ImportType[] = ['sinh-vien', 'chuyen-doi-thpt', 'top149'];

/**
 * Luồng có màn xác nhận ghi đè. Khớp với các luồng server biết phân loại — luồng
 * ngoài danh sách chạy thẳng như trước.
 */
const LOAI_CO_XAC_NHAN_CAP_NHAT = new Set<ImportType>([
	'province',
	'highschool',
	'admission-method',
	'sinh-vien'
]);

const IMPORT_ORDER_GUIDE = `Thứ tự nhập dữ liệu khuyến nghị:
1. Tỉnh thành - Dữ liệu cơ sở, không phụ thuộc
2. Trường THPT - Phụ thuộc: Tỉnh thành
3. Khoa - Dữ liệu cơ sở, không phụ thuộc
4. Ngành - Phụ thuộc: Khoa
5. Phương thức tuyển sinh - Dữ liệu cơ sở, không phụ thuộc
6. Sinh viên - Phụ thuộc: Ngành, Phương thức tuyển sinh, Trường THPT. Trường THPT phải có sẵn trong danh mục; dòng nào mã trường không tra được sẽ bị bỏ qua.
7. Chuyển đổi mã THPT - Phụ thuộc: Trường THPT
8. Top 149 - Phụ thuộc: Trường THPT`;

export const DataImportPage = () => {
	const [activeTab, setActiveTab] = useState(0);
	const { user } = useUser();
	const isAdmin = user?.role === 'admin';
	const importTypes = isAdmin ? allImportTypes : managerAllowedTypes;
	const [tabStates, setTabStates] = useState<Record<ImportType, ImportTabState>>({
		'sinh-vien': { ...initialTabState },
		'chuyen-doi-thpt': { ...initialTabState },
		'top149': { ...initialTabState },
		'province': { ...initialTabState },
		'highschool': { ...initialTabState },
		'faculty': { ...initialTabState },
		'major': { ...initialTabState },
		'admission-method': { ...initialTabState }
	});
	const fileInputRefs = useRef<Record<ImportType, HTMLInputElement | null>>({
		'sinh-vien': null,
		'chuyen-doi-thpt': null,
		'top149': null,
		'province': null,
		'highschool': null,
		'faculty': null,
		'major': null,
		'admission-method': null
	});
	const eventSourceRef = useRef<EventSource | null>(null);
	const { showSuccess, showError } = useSnackbar();

	// Data management state
	const [tableStats, setTableStats] = useState<TableStats[]>([]);
	const [loadingStats, setLoadingStats] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
	const [tableToDelete, setTableToDelete] = useState<string | null>(null);
	const [deleteConfirmation, setDeleteConfirmation] = useState('');
	const [isDeleting, setIsDeleting] = useState(false);

	// Loại import đang chờ xác nhận chuyển bí danh; null = không có dialog nào mở.
	const [xacNhanBiDanh, setXacNhanBiDanh] = useState<ImportType | null>(null);
	// Màn xác nhận ghi đè: luồng nào đang mở, và kết quả phân loại của server.
	const [xacNhanCapNhat, setXacNhanCapNhat] = useState<ImportType | null>(null);
	const [ketQuaPhanLoai, setKetQuaPhanLoai] = useState<KetQuaPhanLoai | null>(null);
	const [dangTaiPhanLoai, setDangTaiPhanLoai] = useState(false);

	const updateTabState = useCallback((type: ImportType, updates: Partial<ImportTabState>) => {
		setTabStates(prev => ({
			...prev,
			[type]: { ...prev[type], ...updates }
		}));
	}, []);

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	};

	const handleDownloadTemplate = async (type: ImportType) => {
		try {
			await importApi.downloadTemplate(type);
			showSuccess(`Đã tải file mẫu ${ImportTypeLabels[type]}`);
		} catch (_error) {
			showError('Không thể tải file mẫu');
		}
	};

	const handleFileSelect = (type: ImportType, event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			updateTabState(type, {
				file,
				validationResult: null,
				importProgress: null,
				importResult: null,
				chuyenBiDanhCu: false
			});
		}
	};

	const handleValidate = async (type: ImportType) => {
		const state = tabStates[type];
		if (!state.file) return;

		updateTabState(type, { isValidating: true, validationResult: null });

		try {
			const result = await importApi.validateFile(type, state.file);
			updateTabState(type, { validationResult: result, isValidating: false });

			if (result.success) {
				showSuccess(`Kiểm tra thành công: ${result.validRows}/${result.totalRows} dòng hợp lệ`);
			} else {
				showError(`Có ${result.errors.length} lỗi cần sửa`);
			}
		} catch (error: unknown) {
			updateTabState(type, { isValidating: false });
			if (isAxiosError(error)) {
				const errorData = error.response?.data?.data;
				if (errorData) {
					updateTabState(type, { validationResult: errorData });
				}
				showError(error.response?.data?.error || 'Lỗi khi kiểm tra file');
			} else {
				showError('Lỗi khi kiểm tra file');
			}
		}
	};

	/**
	 * Hỏi server "phiên này sẽ ghi đè những gì" rồi mở màn xác nhận. Luồng không
	 * có màn này, hoặc không ghi đè bản ghi nào, thì chạy thẳng — không bắt người
	 * dùng bấm thêm một nút vô nghĩa.
	 */
	const handleMoXacNhanCapNhat = async (type: ImportType) => {
		const state = tabStates[type];
		if (!state.validationResult?.sessionId) return;

		if (!LOAI_CO_XAC_NHAN_CAP_NHAT.has(type)) {
			void handleExecuteImport(type);
			return;
		}

		setDangTaiPhanLoai(true);
		try {
			const ketQua = await importApi.previewUpdates(state.validationResult.sessionId);
			if (ketQua.dongCapNhat.length === 0) {
				void handleExecuteImport(type);
				return;
			}
			setKetQuaPhanLoai(ketQua);
			setXacNhanCapNhat(type);
		} catch {
			showError('Không xem trước được thay đổi. Vui lòng thử lại.');
		} finally {
			setDangTaiPhanLoai(false);
		}
	};

	const handleXacNhanCapNhat = async (khoaDuocChon: string[]) => {
		const type = xacNhanCapNhat;
		const sessionId = type ? tabStates[type].validationResult?.sessionId : undefined;
		if (!type || !sessionId) return;

		try {
			await importApi.setUpdateSelection(sessionId, khoaDuocChon);
		} catch {
			showError('Không gửi được lựa chọn. Vui lòng thử lại.');
			return;
		}
		setXacNhanCapNhat(null);
		setKetQuaPhanLoai(null);
		void handleExecuteImport(type);
	};

	const handleExecuteImport = async (type: ImportType) => {
		const state = tabStates[type];
		if (!state.validationResult?.sessionId) return;

		setXacNhanBiDanh(null);
		updateTabState(type, { isImporting: true, importProgress: null, importResult: null });

		eventSourceRef.current = importApi.createExecuteStream(
			type,
			state.validationResult.sessionId,
			(progress) => {
				updateTabState(type, { importProgress: progress });
			},
			(result) => {
				updateTabState(type, {
					isImporting: false,
					importResult: result,
					importProgress: null
				});

				if (result.status === 'completed') {
					showSuccess(result.message || 'Nhập dữ liệu thành công');
				} else if (result.status === 'cancelled') {
					showError('Đã hủy nhập dữ liệu');
				} else {
					showError(result.message || 'Lỗi nhập dữ liệu');
				}
			},
			(error) => {
				updateTabState(type, { isImporting: false });
				showError(error);
			},
			state.chuyenBiDanhCu
		);
	};

	const handleCancelImport = async (type: ImportType) => {
		const state = tabStates[type];
		if (!state.validationResult?.sessionId) return;

		try {
			await importApi.cancelImport(state.validationResult.sessionId);
			showSuccess('Đã gửi yêu cầu hủy');
		} catch (_error) {
			showError('Không thể hủy tiến trình');
		}
	};

	const handleReset = (type: ImportType) => {
		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}
		updateTabState(type, { ...initialTabState });
		if (fileInputRefs.current[type]) {
			fileInputRefs.current[type]!.value = '';
		}
	};

	// Data management handlers
	const loadTableStats = useCallback(async () => {
		setLoadingStats(true);
		try {
			const stats = await dataManagementApi.getTableStats();
			setTableStats(stats);
		} catch (_error) {
			showError('Không thể tải thông tin bảng dữ liệu');
		} finally {
			setLoadingStats(false);
		}
	}, [showError]);

	// Auto-load table stats on component mount - Admin only
	useEffect(() => {
		if (isAdmin) {
			loadTableStats();
		}
	}, [isAdmin, loadTableStats]);

	const openDeleteDialog = (tableName: string) => {
		setTableToDelete(tableName);
		setDeleteDialogOpen(true);
	};

	const closeDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setTableToDelete(null);
	};

	const handleDeleteTable = async () => {
		if (!tableToDelete) return;

		setIsDeleting(true);
		try {
			const result = await dataManagementApi.deleteTable(tableToDelete);
			showSuccess(result.message);
			await loadTableStats();
		} catch (error: unknown) {
			showError(isAxiosError(error) ? error.response?.data?.error || 'Không thể xóa dữ liệu' : 'Không thể xóa dữ liệu');
		} finally {
			setIsDeleting(false);
			closeDeleteDialog();
		}
	};

	const openDeleteAllDialog = () => {
		setDeleteConfirmation('');
		setDeleteAllDialogOpen(true);
	};

	const closeDeleteAllDialog = () => {
		setDeleteAllDialogOpen(false);
		setDeleteConfirmation('');
	};

	const handleDeleteAllData = async () => {
		if (deleteConfirmation !== 'DELETE ALL') return;

		setIsDeleting(true);
		try {
			const result = await dataManagementApi.deleteAllData('DELETE ALL');
			showSuccess(result.message);
			await loadTableStats();
		} catch (error: unknown) {
			showError(isAxiosError(error) ? error.response?.data?.error || 'Không thể xóa toàn bộ dữ liệu' : 'Không thể xóa toàn bộ dữ liệu');
		} finally {
			setIsDeleting(false);
			closeDeleteAllDialog();
		}
	};

	const renderErrorsTable = (errors: ImportError[]) => {
		const columns: GridColDef[] = [
			{ field: 'row', headerName: 'Dòng', width: 80 },
			{ field: 'column', headerName: 'Cột', width: 150 },
			{ field: 'message', headerName: 'Lỗi', flex: 1 },
			{ field: 'value', headerName: 'Giá trị', width: 150 }
		];

		const rows = errors.map((error, index) => ({
			id: index,
			...error
		}));

		return (
			<Box sx={{ height: 300, width: '100%' }}>
				<DataGrid
					rows={rows}
					columns={columns}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } }
					}}
					pageSizeOptions={[10, 25, 50]}
					density="compact"
					disableRowSelectionOnClick
					sx={{
						'& .MuiDataGrid-footerContainer': {
							overflow: 'hidden',
						},
						'& .MuiTablePagination-root': {
							overflow: 'hidden',
						},
					}}
				/>
			</Box>
		);
	};

	const renderPreviewTable = (preview: Record<string, unknown>[]) => {
		if (!preview || preview.length === 0) return null;

		const columns: GridColDef[] = Object.keys(preview[0]).map(key => ({
			field: key,
			headerName: key,
			minWidth: 120,
			flex: 1
		}));

		const rows = preview.map((row, index) => ({ id: index, ...row }));

		return (
			<Box sx={{ height: 350, width: '100%', mt: 2 }}>
				<Typography variant="subtitle2" gutterBottom>
					Xem trước dữ liệu ({preview.length} dòng đầu, {columns.length} cột)
				</Typography>
				<DataGrid
					rows={rows}
					columns={columns}
					initialState={{
						pagination: { paginationModel: { pageSize: 5 } }
					}}
					pageSizeOptions={[5, 10]}
					density="compact"
					disableRowSelectionOnClick
					sx={{
						'& .MuiDataGrid-footerContainer': {
							overflow: 'hidden',
						},
						'& .MuiTablePagination-root': {
							overflow: 'hidden',
						},
					}}
				/>
			</Box>
		);
	};

	const renderImportContent = (type: ImportType) => {
		const state = tabStates[type];

		return (
			<Stack spacing={3}>
				{/* File Upload Section */}
				<Card>
					<CardContent>
						<Stack spacing={2}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
								<Typography variant="h6">Tải lên file dữ liệu</Typography>
								<Button
									variant="outlined"
									startIcon={<DownloadIcon />}
									onClick={() => handleDownloadTemplate(type)}
									size="small"
								>
									Tải file mẫu
								</Button>
							</Box>

							<Box
								sx={{
									border: '2px dashed',
									borderColor: state.file ? 'primary.main' : 'grey.300',
									borderRadius: 2,
									p: 3,
									textAlign: 'center',
									cursor: 'pointer',
									transition: 'all 0.2s',
									'&:hover': {
										borderColor: 'primary.main',
										bgcolor: 'action.hover'
									}
								}}
								onClick={() => fileInputRefs.current[type]?.click()}
							>
								<input
									type="file"
									accept=".xlsx,.csv"
									hidden
									ref={(el) => { fileInputRefs.current[type] = el; }}
									onChange={(e) => handleFileSelect(type, e)}
								/>
								<CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
								<Typography variant="body1" color="text.secondary">
									{state.file ? state.file.name : 'Kéo thả file hoặc nhấp để chọn'}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Chấp nhận: Excel (.xlsx) hoặc CSV - Tối đa 10MB
								</Typography>
							</Box>

							{state.file && (
								<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
									<Button
										variant="outlined"
										color="inherit"
										onClick={() => handleReset(type)}
										startIcon={<RefreshIcon />}
									>
										Chọn file khác
									</Button>
									<Button
										variant="contained"
										onClick={() => handleValidate(type)}
										disabled={state.isValidating || state.isImporting}
										startIcon={state.isValidating ? undefined : <CheckCircleIcon />}
									>
										{state.isValidating ? 'Đang kiểm tra...' : 'Kiểm tra dữ liệu'}
									</Button>
								</Box>
							)}

							{state.isValidating && <LinearProgress />}
						</Stack>
					</CardContent>
				</Card>

				{/* Validation Results */}
				{state.validationResult && (
					<Card>
						<CardContent>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									{state.validationResult.success ? (
										<CheckCircleIcon color="success" />
									) : (
										<ErrorIcon color="error" />
									)}
									<Typography variant="h6">
										Kết quả kiểm tra
									</Typography>
									<Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
										<Chip
											label={`Tổng: ${state.validationResult.totalRows}`}
											size="small"
											color="default"
										/>
										<Chip
											label={`Hợp lệ: ${state.validationResult.validRows}`}
											size="small"
											color="success"
										/>
										{state.validationResult.errors.length > 0 && (
											<Chip
												label={`Lỗi: ${state.validationResult.errors.length}`}
												size="small"
												color="error"
											/>
										)}
									</Box>
								</Box>

								{state.validationResult.success ? (
									<Alert severity="success">
										<AlertTitle>Dữ liệu hợp lệ</AlertTitle>
										Tất cả {state.validationResult.validRows} dòng đều hợp lệ và sẵn sàng để nhập.
									</Alert>
								) : (
									<Alert severity="warning">
										<AlertTitle>Có lỗi cần sửa</AlertTitle>
										Vui lòng sửa {state.validationResult.errors.length} lỗi trong file và tải lên lại.
									</Alert>
								)}

								{state.validationResult.warnings && state.validationResult.warnings.length > 0 && (
									<Stack spacing={1}>
										{state.validationResult.warnings.map((warning, index) => (
											<Alert key={index} severity="warning">
												{warning}
											</Alert>
										))}
									</Stack>
								)}

								{state.validationResult.errors.length > 0 && (
									<Box>
										<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
											<Typography variant="subtitle2">
												Chi tiết lỗi
											</Typography>
											<Button
												size="small"
												variant="outlined"
												startIcon={<DownloadIcon />}
												sx={{ ml: 'auto' }}
												onClick={() =>
													downloadImportErrorsCsv(
														state.validationResult?.errors,
														[],
														`loi-kiem-tra-${type}`
													)
												}
											>
												Tải CSV lỗi
											</Button>
										</Box>
										{renderErrorsTable(state.validationResult.errors)}
									</Box>
								)}

								{state.validationResult.preview && state.validationResult.preview.length > 0 && (
									renderPreviewTable(state.validationResult.preview)
								)}

								{type === 'chuyen-doi-thpt' && (state.validationResult.soBiDanhSeChuyen ?? 0) > 0 && (
									<Alert severity="warning" icon={<WarningIcon />}>
										<AlertTitle>Bí danh cũ đang giữ mã đích</AlertTitle>
										<Typography variant="body2" sx={{ mb: 1 }}>
											{state.validationResult.soBiDanhSeChuyen} mã đích trong file đang là bí danh
											của trường khác. Để tắt thì từng ấy dòng sẽ bị bỏ qua; bật thì bí danh được
											gỡ khỏi chủ cũ và mã về chủ mới theo danh mục. Hoàn tác lô sẽ trả lại.
										</Typography>
										<FormControlLabel
											control={
												<Switch
													checked={state.chuyenBiDanhCu}
													onChange={(e) =>
														updateTabState(type, { chuyenBiDanhCu: e.target.checked })
													}
													disabled={state.isImporting}
												/>
											}
											label="Chấp nhận xoá bí danh cũ"
										/>
									</Alert>
								)}

								{state.validationResult.success && (
									<Box
										sx={{
											mt: 4,
											display: 'flex', justifyContent: 'flex-end', gap: 2
										}}>
										<Button
											sx={{ mt: 4 }}
											variant="contained"
											color="primary"
											onClick={() =>
												state.chuyenBiDanhCu
													? setXacNhanBiDanh(type)
													: handleMoXacNhanCapNhat(type)
											}
											disabled={state.isImporting || dangTaiPhanLoai}
											startIcon={<PlayArrowIcon />}
										>
											Bắt đầu nhập dữ liệu
										</Button>
									</Box>
								)}
							</Stack>
						</CardContent>
					</Card>
				)}

				{/* Import Progress */}
				{(state.isImporting || state.importProgress) && (
					<Card>
						<CardContent>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Typography variant="h6">Tiến trình nhập liệu</Typography>
									{state.isImporting && (
										<Tooltip title="Hủy nhập liệu">
											<IconButton
												color="error"
												onClick={() => handleCancelImport(type)}
												size="small"
											>
												<CancelIcon />
											</IconButton>
										</Tooltip>
									)}
								</Box>

								<Box sx={{ width: '100%' }}>
									<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
										<Typography variant="body2" color="text.secondary">
											{state.importProgress?.message || 'Đang xử lý...'}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{state.importProgress?.percentage || 0}%
										</Typography>
									</Box>
									<LinearProgress
										variant="determinate"
										value={state.importProgress?.percentage || 0}
									/>
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										{state.importProgress?.currentRow || 0} / {state.importProgress?.totalRows || 0} dòng
									</Typography>
								</Box>
							</Stack>
						</CardContent>
					</Card>
				)}

				{/* Import Result */}
				{state.importResult && (
					<Card>
						<CardContent>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									{state.importResult.status === 'completed' ? (
										<CheckCircleIcon color="success" fontSize="large" />
									) : state.importResult.status === 'cancelled' ? (
										<WarningIcon color="warning" fontSize="large" />
									) : (
										<ErrorIcon color="error" fontSize="large" />
									)}
									<Box>
										<Typography variant="h6">
											{state.importResult.status === 'completed'
												? 'Hoàn tất nhập liệu'
												: state.importResult.status === 'cancelled'
													? 'Đã hủy nhập liệu'
													: 'Lỗi nhập liệu'}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{state.importResult.message}
										</Typography>
									</Box>
								</Box>

								{/* Chi tiết hiển thị cho CẢ lần chạy hỏng, không chỉ lần thành công:
								    khi import thất bại thì lý do bỏ qua và dòng lỗi chính là thứ
								    người vận hành cần nhất, mà backend đã gửi sẵn kèm sự kiện cuối. */}
								{(state.importResult.status === 'completed' || state.importResult.status === 'error') && (
									<>
										<Box sx={{ display: 'flex', gap: 2 }}>
											<Chip
												icon={<CheckCircleIcon />}
												label={`Mới: ${state.importResult.importedCount || 0}`}
												color="success"
												variant="outlined"
											/>
											<Chip
												icon={<RefreshIcon />}
												label={`Cập nhật: ${state.importResult.updatedCount || 0}`}
												color="info"
												variant="outlined"
											/>
											<Chip
												icon={<WarningIcon />}
												label={`Bỏ qua: ${state.importResult.skippedCount || 0}`}
												color="warning"
												variant="outlined"
											/>
										</Box>

										{((state.importResult.errors && state.importResult.errors.length > 0) ||
											(state.importResult.skippedReasons && state.importResult.skippedReasons.length > 0)) && (
											<Box>
												<Button
													size="small"
													variant="outlined"
													startIcon={<DownloadIcon />}
													onClick={() =>
														downloadImportErrorsCsv(
															state.importResult?.errors,
															state.importResult?.skippedReasons,
															`loi-nhap-${type}`
														)
													}
												>
													Tải CSV lỗi
												</Button>
											</Box>
										)}

										{state.importResult.skippedReasons && state.importResult.skippedReasons.length > 0 && (
											<Box>
												<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
													Chi tiết lý do bỏ qua:
												</Typography>
												<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
													{state.importResult.skippedReasons.map((skipReason, index) => (
														<Chip
															key={index}
															label={`${skipReason.reason}: ${skipReason.count}`}
															color="warning"
															size="small"
															variant="filled"
														/>
													))}
												</Box>
											</Box>
										)}

										{state.importResult.errors && state.importResult.errors.length > 0 && (
											<Box>
												<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
													Dòng gây lỗi:
												</Typography>
												{renderErrorsTable(state.importResult.errors)}
											</Box>
										)}
									</>
								)}
								<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
									<Button
										variant="outlined"
										onClick={() => handleReset(type)}
										startIcon={<RefreshIcon />}
									>
										Nhập file mới
									</Button>
								</Box>
							</Stack>
						</CardContent>
					</Card>
				)}
			</Stack>
		);
	};

	return (
		<BasePageLayout>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
					Nhập dữ liệu
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Nhập dữ liệu từ file Excel/CSV và quản lý dữ liệu hệ thống
				</Typography>
			</Box>

			{/* Import Order Guide - Admin only */}
			{isAdmin && (
				<Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
					<AlertTitle>Hướng dẫn thứ tự nhập dữ liệu</AlertTitle>
					<Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
						{IMPORT_ORDER_GUIDE}
					</Typography>
				</Alert>
			)}

			<Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 3 }}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					sx={{ borderBottom: 1, borderColor: 'divider' }}
					variant="scrollable"
					scrollButtons="auto"
				>
					{importTypes.map((type, index) => (
						<Tab key={type} label={ImportTypeLabels[type]} id={`import-tab-${index}`} />
					))}
				</Tabs>

				<Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
					{importTypes.map((type, index) => (
						<TabPanel key={type} value={activeTab} index={index}>
							{renderImportContent(type)}
						</TabPanel>
					))}
				</Box>
			</Paper>

			{/* Data Management Section - Admin only */}
			{isAdmin && (
				<>
					<Divider sx={{ my: 3 }} />
					<Box sx={{ mb: 3 }}>
						<Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
							Quản lý dữ liệu
						</Typography>
						<Typography variant="body1" color="text.secondary" gutterBottom>
							Xóa dữ liệu từng bảng hoặc toàn bộ hệ thống
						</Typography>
					</Box>

					<Card sx={{ mb: 3 }}>
						<CardContent>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Typography variant="h6">Thống kê dữ liệu</Typography>
									<Button
										variant="outlined"
										startIcon={<RefreshIcon />}
										onClick={loadTableStats}
										disabled={loadingStats}
										size="small"
									>
										{loadingStats ? 'Đang tải...' : 'Làm mới'}
									</Button>
								</Box>

								{tableStats.length === 0 && !loadingStats && (
									<Alert severity="info">
										Nhấn "Làm mới" để tải thông tin các bảng dữ liệu
									</Alert>
								)}

								{tableStats.length > 0 && (
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Bảng dữ liệu</TableCell>
													<TableCell align="right">Số bản ghi</TableCell>
													<TableCell align="right">Thao tác</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{tableStats.map((stat) => (
													<TableRow key={stat.table}>
														<TableCell>{stat.displayName}</TableCell>
														<TableCell align="right">{stat.count.toLocaleString()}</TableCell>
														<TableCell align="right">
															<Tooltip title="Xóa dữ liệu bảng này">
																<span>
																	<IconButton
																		color="error"
																		size="small"
																		onClick={() => openDeleteDialog(stat.table)}
																		disabled={stat.count === 0 || isDeleting}
																	>
																		<DeleteIcon />
																	</IconButton>
																</span>
															</Tooltip>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								)}

								<Divider />

								<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
									<Button
										variant="contained"
										color="error"
										startIcon={<DeleteForeverIcon />}
										onClick={openDeleteAllDialog}
										disabled={isDeleting}
									>
										Xóa toàn bộ dữ liệu
									</Button>
								</Box>
							</Stack>
						</CardContent>
					</Card>
				</>
			)}

			<ImportUpdateConfirmDialog
				open={xacNhanCapNhat !== null}
				tenLoai={xacNhanCapNhat ? ImportTypeLabels[xacNhanCapNhat] : ''}
				ketQua={ketQuaPhanLoai}
				onHuy={() => {
					setXacNhanCapNhat(null);
					setKetQuaPhanLoai(null);
				}}
				onXacNhan={handleXacNhanCapNhat}
			/>

			{/* Delete Single Table Dialog - Admin only */}
			<Dialog open={isAdmin && deleteDialogOpen} onClose={closeDeleteDialog}>
				<DialogTitle>Xác nhận xóa dữ liệu</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Bạn có chắc chắn muốn xóa toàn bộ dữ liệu của bảng{' '}
						<strong>{tableStats.find(s => s.table === tableToDelete)?.displayName}</strong>?
						<br /><br />
						<Alert severity="warning" sx={{ mt: 1 }}>
							Hành động này sẽ xóa tất cả bản ghi và các dữ liệu phụ thuộc liên quan.
							Không thể hoàn tác!
						</Alert>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDeleteDialog} disabled={isDeleting}>
						Hủy
					</Button>
					<Button
						onClick={handleDeleteTable}
						color="error"
						variant="contained"
						disabled={isDeleting}
					>
						{isDeleting ? 'Đang xóa...' : 'Xóa'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete All Data Dialog - Admin only */}
			<Dialog open={isAdmin && deleteAllDialogOpen} onClose={closeDeleteAllDialog}>
				<DialogTitle sx={{ color: 'error.main' }}>
					Xác nhận xóa toàn bộ dữ liệu
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<Alert severity="error" sx={{ mb: 2 }}>
							<strong>CẢNH BÁO:</strong> Hành động này sẽ xóa TOÀN BỘ dữ liệu ứng dụng
							(không bao gồm tài khoản người dùng). Không thể hoàn tác!
						</Alert>
						Để xác nhận, vui lòng nhập <strong>DELETE ALL</strong> vào ô bên dưới:
					</DialogContentText>
					<TextField
						autoFocus
						margin="dense"
						fullWidth
						value={deleteConfirmation}
						onChange={(e) => setDeleteConfirmation(e.target.value)}
						placeholder="DELETE ALL"
						error={deleteConfirmation !== '' && deleteConfirmation !== 'DELETE ALL'}
						helperText={deleteConfirmation !== '' && deleteConfirmation !== 'DELETE ALL'
							? 'Nhập chính xác "DELETE ALL"'
							: ''
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDeleteAllDialog} disabled={isDeleting}>
						Hủy
					</Button>
					<Button
						onClick={handleDeleteAllData}
						color="error"
						variant="contained"
						disabled={isDeleting || deleteConfirmation !== 'DELETE ALL'}
					>
						{isDeleting ? 'Đang xóa...' : 'Xóa toàn bộ'}
					</Button>
				</DialogActions>
			</Dialog>
			{/* Xác nhận chuyển bí danh — nói rõ CON SỐ trước khi ghi */}
			<Dialog open={xacNhanBiDanh !== null} onClose={() => setXacNhanBiDanh(null)}>
				<DialogTitle>Xác nhận chuyển bí danh cũ</DialogTitle>
				<DialogContent>
					<DialogContentText component="div">
						<Alert severity="warning" sx={{ mb: 2 }}>
							<strong>
								{xacNhanBiDanh
									? (tabStates[xacNhanBiDanh].validationResult?.soBiDanhSeChuyen ?? 0)
									: 0}{' '}
								bí danh
							</strong>{' '}
							sẽ được gỡ khỏi trường đang giữ để nhường mã cho chủ mới theo danh mục.
						</Alert>
						Sau khi chạy, tra cứu bằng những mã đó sẽ ra chủ mới. Mọi bí danh bị gỡ đều được ghi
						lại trong lô này — hoàn tác lô sẽ trả lại nguyên trạng.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setXacNhanBiDanh(null)}>Hủy</Button>
					<Button
						onClick={() => xacNhanBiDanh && handleExecuteImport(xacNhanBiDanh)}
						color="warning"
						variant="contained"
					>
						Đồng ý và nhập
					</Button>
				</DialogActions>
			</Dialog>
		</BasePageLayout>
	);
};

export default DataImportPage;
